import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 8;
  const canSubmit = useMemo(
    () => token && password.length >= 8 && password === confirm && !loading,
    [token, password, confirm, loading]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Falta el token de restablecimiento en la URL.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      // Send the user back to login after a brief confirmation
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "No pudimos restablecer tu contraseña. El enlace puede haber expirado."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-950 p-8"
        data-testid="reset-password-page"
      >
        <div className="w-full max-w-md space-y-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h2
            className="text-2xl font-bold text-white"
            data-testid="reset-password-missing-token"
          >
            Enlace inválido
          </h2>
          <p className="text-slate-400 text-sm">
            No encontramos un token de restablecimiento en la URL. Solicita un
            nuevo enlace desde la página de inicio de sesión.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block text-teal-400 hover:text-teal-300 font-medium text-sm"
          >
            Solicitar enlace de nuevo
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-950 p-8"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/notalia-icon.png"
            alt="Notalia"
            className="h-10 mx-auto mb-6 brightness-0 invert"
          />
          <h2
            className="text-2xl font-bold text-white"
            data-testid="reset-password-heading"
          >
            Crea una nueva contraseña
          </h2>
          <p className="text-slate-400 mt-2 text-sm">
            Elige una contraseña segura de al menos 8 caracteres.
          </p>
        </div>

        {success ? (
          <div
            className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-5 py-6 rounded-lg text-center space-y-3"
            data-testid="reset-password-success"
          >
            <CheckCircle2 className="w-10 h-10 mx-auto" />
            <p className="text-sm leading-relaxed">
              ¡Tu contraseña se restableció correctamente! Te redirigimos al
              inicio de sesión…
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm"
                data-testid="reset-password-error"
              >
                {error}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="reset-password-form"
            >
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  Nueva contraseña
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    required
                    autoFocus
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20 pl-10 pr-10"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="reset-password-toggle-visibility"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {tooShort && (
                  <p
                    className="text-xs text-amber-400"
                    data-testid="reset-password-too-short"
                  >
                    Necesita al menos 8 caracteres.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-slate-300">
                  Confirmar contraseña
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="confirm"
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repite la contraseña"
                    required
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20 pl-10"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                {mismatch && (
                  <p
                    className="text-xs text-red-400"
                    data-testid="reset-password-mismatch"
                  >
                    Las contraseñas no coinciden.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full bg-teal-600 hover:bg-teal-500 text-white font-medium h-11"
                data-testid="reset-password-submit-button"
              >
                {loading ? "Restableciendo..." : "Restablecer contraseña"}
              </Button>
            </form>

            <Link
              to="/login"
              className="block text-center text-sm text-slate-400 hover:text-teal-300"
              data-testid="reset-back-to-login-link"
            >
              Volver al inicio de sesión
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
